import * as React from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * One option in the multi-select dropdown. `value` is the canonical id sent
 * to the parent; `label` is what the user sees.
 */
export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectProps {
  /** Currently selected values. Order is preserved across re-renders. */
  value: string[];
  onChange: (value: string[]) => void;
  options: MultiSelectOption[];
  /** Trigger text when 0 options are selected. */
  placeholder?: string;
  /**
   * Builds the trigger label when `value.length > 0`. Receives the selected
   * option objects so callers can render things like "Status: PENDING +2".
   * Defaults to a comma-joined label list.
   */
  renderLabel?: (selected: MultiSelectOption[]) => string;
  disabled?: boolean;
  /** Applied to the wrapper, which is what positions the panel. */
  className?: string;
  /**
   * Applied to the trigger button itself. Needed for compact placements — the
   * default is a full-height form control, and `className` can't reach it.
   */
  triggerClassName?: string;
  panelClassName?: string;
  /** Optional emptied helper — a "Clear" link inside the panel. */
  showClear?: boolean;
  clearLabel?: string;
  /**
   * Override for what the "Clear" link does. Defaults to `onChange([])`.
   * Useful when callers want "Clear" to mean "reset to default selection"
   * instead of "select nothing".
   */
  onClear?: () => void;
}

/**
 * Multi-select dropdown with a checkbox list. Click outside or Escape to
 * close. Selection is preserved across reopens. Matches the visual language
 * of {@link Combobox} (same trigger height, border, chevron) so the admin
 * filters bar stays uniform.
 */
export function MultiSelect({
  value,
  onChange,
  options,
  placeholder = 'None selected',
  renderLabel,
  disabled = false,
  className,
  triggerClassName,
  panelClassName,
  showClear = false,
  clearLabel = 'Clear',
  onClear,
}: MultiSelectProps): JSX.Element {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const selectedSet = React.useMemo(() => new Set(value), [value]);
  const selectedOptions = React.useMemo(
    () => options.filter((o) => selectedSet.has(o.value)),
    [options, selectedSet],
  );

  function toggle(next: string): void {
    if (selectedSet.has(next)) {
      onChange(value.filter((v) => v !== next));
    } else {
      onChange([...value, next]);
    }
  }

  React.useEffect(() => {
    if (!open) return;
    function handleClickOutside(ev: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(ev.target as Node)) {
        setOpen(false);
      }
    }
    function handleKeyDown(ev: KeyboardEvent): void {
      if (ev.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.blur();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const triggerLabel =
    selectedOptions.length === 0
      ? placeholder
      : renderLabel
        ? renderLabel(selectedOptions)
        : selectedOptions.map((o) => o.label).join(', ');

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 pr-8 text-sm transition-colors duration-150 ease-out-quart pointer-coarse:h-11 pointer-coarse:text-base',
          'focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
          'disabled:cursor-not-allowed disabled:opacity-50',
          selectedOptions.length === 0 && 'text-muted-foreground/70',
          triggerClassName,
        )}
      >
        <span className="truncate text-left">{triggerLabel}</span>
        <ChevronDown
          className={cn(
            'absolute right-3 h-3.5 w-3.5 text-muted-foreground transition-transform duration-150',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          className={cn(
            'absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-border bg-popover p-1 shadow-overlay',
            panelClassName,
          )}
        >
          <div className="max-h-[300px] overflow-auto p-1">
            {options.length === 0 ? (
              <div className="px-2 py-6 text-center text-xs text-muted-foreground">
                No options
              </div>
            ) : (
              options.map((item) => {
                const isSelected = selectedSet.has(item.value);
                return (
                  <button
                    key={item.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => toggle(item.value)}
                    className={cn(
                      'flex w-full select-none items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors',
                      'hover:bg-accent hover:text-accent-foreground',
                      isSelected && 'text-foreground',
                    )}
                  >
                    <span
                      className={cn(
                        'grid h-4 w-4 place-items-center rounded-sm border',
                        isSelected
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-input',
                      )}
                      aria-hidden="true"
                    >
                      {isSelected && <Check className="h-3 w-3" />}
                    </span>
                    <span className="flex-1 truncate">{item.label}</span>
                  </button>
                );
              })
            )}
          </div>
          {showClear && (
            <div className="border-t border-border p-1">
              <button
                type="button"
                onClick={() => (onClear ? onClear() : onChange([]))}
                className="w-full rounded-sm px-2 py-1.5 text-left text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              >
                {clearLabel}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
